import styled from 'styled-components';
import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ApolloQueryResult } from '@apollo/client';
import { TabBar } from '~/singlePageApp/components/TabBar';
import { WistiaPlayerProvider } from '~/hooks/player/useWistiaPlayer';
import type { PublicCommentsSelection } from '~/graphql';
import { useMq } from '@wistia/vhs';
import { isNil } from '~/utilities/type-guards';
import { WistiaTranscript } from '~/components/WistiaTranscript';
import type { WistiaPlayer } from '@wistia/wistia-player';
import type { AnonymousMediaQuery } from './__gql/AnonymousMedia.gql.gen';
import { CommentContextProvider, commentingViews } from '../Media/Comments/CommentContext';
import { Comments } from './Comments';
import type { MediaPreviewQuery } from '../MediaPreview/__gql/MediaPreview.gql.gen';
import { MediaStoreSelectors, useMediaStore } from '../Media/Store/MediaStore';

const COMMENTS = 'Comments';
const TRANSCRIPT = 'Transcript';

const TAB_BAR_HEIGHT = 35;

const COMMENT_VIEWS = new Map([
  ['team', commentingViews.team],
  ['audience', commentingViews.audience],
]);

type TabName = 'Comments' | 'Transcript';
type TabbedAreaProps = {
  fontFamily?: string;
  shouldShowTranscript: boolean;
  videoEmbed: WistiaPlayer | null;
  audienceComments:
    | NonNullable<AnonymousMediaQuery['anonymousMedia']>['topLevelAudienceComments']
    | NonNullable<MediaPreviewQuery['media']>['topLevelAudienceComments'];
  teamComments:
    | NonNullable<AnonymousMediaQuery['anonymousMedia']>['topLevelTeamComments']
    | NonNullable<MediaPreviewQuery['media']>['topLevelTeamComments'];
  mediaId: string;
  mediaHashedId: string;
  publicCommentsSelection: PublicCommentsSelection;
  commentingDisabled: boolean;
  fetchMore?: (arg: GenericRecord) => Promise<ApolloQueryResult<AnonymousMediaQuery>>;
};

const TranscriptWrapper = styled.div`
  margin-top: 1em;
`;

const TabBarWrapper = styled.div.attrs<{ $stickyOffset: number }>(({ $stickyOffset }) => ({
  style: {
    top: `${$stickyOffset}px`,
  },
}))<{ $stickyOffset: number }>`
  position: sticky;
  top: 0;
  z-index: 1;
`;

export const TabbedArea = ({
  shouldShowTranscript,
  videoEmbed,
  audienceComments,
  teamComments,
  mediaId,
  mediaHashedId,
  publicCommentsSelection,
  commentingDisabled,
  fetchMore,
  fontFamily,
}: TabbedAreaProps): JSX.Element => {
  const { isXlAndUp } = useMq();

  const shouldShowComments =
    publicCommentsSelection === 'audience' || (publicCommentsSelection === 'team' && !isXlAndUp);

  const tabNames = useMemo(() => {
    const result: TabName[] = [];
    if (shouldShowTranscript) {
      result.push(TRANSCRIPT);
    }
    if (shouldShowComments) {
      result.push(COMMENTS);
    }
    return result;
  }, [shouldShowTranscript, shouldShowComments]);

  const initialTabName = tabNames.length > 0 ? tabNames[0] : null;

  const [selectedTab, setSelectedTab] = useState(initialTabName);

  const onCommentClick = useCallback(
    (commentId: string | null, timestamp?: number) => {
      if (isNil(commentId) || isNil(timestamp) || isNil(videoEmbed)) {
        return;
      }

      // eslint-disable-next-line no-param-reassign
      videoEmbed.currentTime = timestamp;
    },
    [videoEmbed],
  );

  // We need this effect because the list of tabs (and thus initialTabName) can
  // change when you're toggling transcript or comments on/off in the media page
  // editor panel. If we didn't setSelectedTab when that happens, it'd be
  // possible for the selected tab to disappear and then have no selected tab
  // (even if another tab still exists).
  useEffect(() => {
    setSelectedTab(initialTabName);
  }, [initialTabName]);

  const playerStickyPosition = useMediaStore(MediaStoreSelectors.getPlayerStickyPosition);
  const stickyOffset = useMemo(() => {
    if (videoEmbed == null) {
      return 0;
    }

    if (playerStickyPosition === 'top') {
      // use the new api if embed is the web component
      return videoEmbed.offsetHeight || 0; // note that prior this could have been a fraction, but offsetHeight is in whole numbers
    }
    return 0;
  }, [videoEmbed, playerStickyPosition]);

  return (
    <WistiaPlayerProvider player={videoEmbed}>
      {tabNames.length > 0 && selectedTab != null ? (
        <TabBarWrapper $stickyOffset={stickyOffset}>
          <TabBar
            activeTab={selectedTab}
            onTabClick={setSelectedTab}
            tabs={tabNames}
          />
        </TabBarWrapper>
      ) : null}
      {videoEmbed ? (
        <>
          {shouldShowComments && selectedTab === COMMENTS ? (
            <CommentContextProvider
              defaultCommentingView={COMMENT_VIEWS.get(publicCommentsSelection)}
              isAnonymousMedia={true}
              onCommentClick={onCommentClick}
            >
              <Comments
                mediaId={mediaId}
                mediaHashedId={mediaHashedId}
                audienceComments={audienceComments}
                teamComments={teamComments}
                commentingDisabled={commentingDisabled}
                fetchMore={fetchMore}
              />
            </CommentContextProvider>
          ) : null}
          {shouldShowTranscript && selectedTab === TRANSCRIPT ? (
            <TranscriptWrapper>
              <WistiaTranscript
                mediaId={mediaHashedId}
                autoScrollMarginTopPx={TAB_BAR_HEIGHT + stickyOffset}
                font={{ family: fontFamily ?? 'inherit' }}
              />
            </TranscriptWrapper>
          ) : null}
        </>
      ) : null}
    </WistiaPlayerProvider>
  );
};
